/**
 * @file Renders the drawer that loads and displays a single investment.
 */
import { useCallback, useEffect, useState } from "react";
import { Spin } from "antd";

import { wait } from "@api";
import { Drawer } from "@components/Page";
import { Markdown } from "@components/Text";
import { W0, HEADER_HEIGHT, DRAWER_WIDTH3 } from "@components/Constants";

import Title from "./Title";
import Investment from "./Investment";
import useInvestmentStore from "./stores/useInvestmentStore";
import type { DrawerRef, Investment as InvestmentItem } from "./types";

const LABEL_ERROR_NOT_FOUND = `Investment **{ID}** is not found. URL is not valid
or investment was deleted.`;

const CLOSE_DELAY = 300;

type Props = {
  drawer: DrawerRef;
  onClose?: () => void;
  onInvestmentUpdated?: (investment: InvestmentItem) => void;
};

/**
 * Renders the investment drawer, loading the investment on open and showing an error when it is not found.
 */
const InvestmentDrawer = ({
  onClose = undefined,
  drawer,
  onInvestmentUpdated = undefined,
}: Props) => {
  const [ error, setError ] = useState<string | undefined>();

  const onInvestmentNotFound = useCallback((id: string) =>
    setError(LABEL_ERROR_NOT_FOUND.replace("{ID}", id))
  , []);

  const {
    account,
    isLoading,
    investment,
    setInvestment,
    readInvestment,
    updateInvestment,
    projectTemplatesStore,
  } = useInvestmentStore(onInvestmentNotFound);

  // NOTE: the parameter is optional because TypeScript infers the onOpen
  //       prop of the JavaScript Drawer component from its empty default.
  /**
   * Loads the investment for the drawer when it opens.
   */
  const onOpen = (id?: string) => readInvestment(id);

  /**
   * Clears the error and schedules the investment to be cleared after the drawer closes.
   */
  const onDrawerClose = () => {
    setError(undefined);
    wait(CLOSE_DELAY).then(() => setInvestment(undefined));

    if (onClose) {
      onClose();
    }
  };

  const isReady =
    investment !== undefined &&
    !isLoading;

  const styles = error
    ? {}
    : {
      body: { paddingTop: W0 },
      header: { minHeight: HEADER_HEIGHT + 1 },
    };

  const title = <Title investment={investment} />;

  const drawerProps = {
    width: DRAWER_WIDTH3,
    onClose: onDrawerClose,
    title,
    drawer,
    onOpen,
    styles,
  };

  useEffect(() => {
    if (!investment) {
      return;
    }

    if (!onInvestmentUpdated) {
      return;
    }

    onInvestmentUpdated(investment);
  }, [ investment, onInvestmentUpdated ]);

  /**
   * Renders the error message, a loading spinner, or the investment, depending on the drawer's state.
   */
  const renderChildren = () => {
    if (error) {
      return (
        <center>
          <Markdown text={error} />
        </center>
      );
    }

    if (!isReady) {
      return <center><Spin /></center>;
    }

    return (
      <Investment
        account={account}
        investment={investment}
        readInvestment={readInvestment}
        updateInvestment={updateInvestment}
        projectTemplatesStore={projectTemplatesStore}
      />
    );
  };

  return (
    <Drawer {...drawerProps}>
      {renderChildren()}
    </Drawer>
  );
};

export default InvestmentDrawer;
